import { memo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { pick, uniq } from 'lodash-es'
import { Table } from '@mantine/core'
import { Button, Modal, Toast } from '@ow/comps'
import { useTbViewStore, useAppEx } from '@vt/hooks'
import useRouteTo from '../hooks/navigation/use-route-to'
import useAreaStore from './areaStore'

const TypeTitle = {
  area: '点位',
  standard: '指标',
}

const Confirm = ({ type }) => {
  const title = TypeTitle[type]
  return (
    <div>
      确定删除当前{title}？{title}删除后不可恢复。
    </div>
  )
}

const List = ({ data, type, closeBatchDelete }) => {
  const title = TypeTitle[type]
  const navs = useAppEx((x) => x.currentNav.navs)
  const appId = useAppEx((x) => x.appId)

  const { transUrl } = useRouteTo({ appId })
  const navigate = useNavigate()

  const batchEdit = () => {
    const tbMap = {}
    const setTbMap = (tbId, objectIds) => {
      if (tbMap[tbId]) {
        tbMap[tbId] = uniq([...tbMap[tbId], ...objectIds])
      } else {
        tbMap[tbId] = objectIds
      }
    }
    data.forEach(({ tbId, tableDatas }) => {
      setTbMap(
        tbId,
        tableDatas.map((item) => item.objectId)
      )
    })

    const tbIds = Object.keys(tbMap)
    const [tbId] = tbIds

    const findNav = (nav) => {
      if (Array.isArray(nav.children)) {
        return nav.children.find(findNav)
      }
      return nav.url.includes(tbId)
    }
    const nav = navs.find(findNav)
    closeBatchDelete()
    navigate(transUrl(nav.url), {
      state: {
        actionType: 'batchEdit',
        payload: { ids: tbMap[tbId], slug: type },
      },
    })
  }
  return (
    <div>
      <div className="mb-4 flex justify-between">
        <span>以下{title}存在案件，需要先转移案件，才能删除！</span>
        <Button type="primary" onClick={batchEdit}>
          转移案件
        </Button>
      </div>
      <Table>
        <thead>
          <tr className="bg-primary-50">
            <th>末级{title}名称</th>
            <th>表单名称</th>
            <th>表单ID</th>
            <th>案件编号</th>
          </tr>
        </thead>
        <tbody>
          {data.map(({ areaId, areaName, tbId, tbName, tableDatas }) => (
            <tr key={`${areaId}-${tbId}`}>
              <td>{areaName}</td>
              <td>{tbName}</td>
              <td>{tbId}</td>
              <td className="break-all max-w-xs">
                {tableDatas.map((item) => item.tableDataId).join(',')}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  )
}

const footer = (confirm, onClose, loading) => (
  <div className="flex gap-3">
    <Button type="primary" onClick={confirm} loading={loading}>
      确定
    </Button>
    <Button className={loading ? 'hidden' : 'visible'} stroke onClick={onClose}>
      取消
    </Button>
  </div>
)

const BatchDelete = ({ type }) => {
  const [open, setOpen] = useState(false)
  const [status, setStatus] = useState(0) // 0:确认 1:删除提示
  const [lockList, setLockList] = useState([])
  const [loading, setLoading] = useState(false)

  const delRef = useRef({})

  const modalSize = ['sm', 'lg']

  const { setAreaState } = useAreaStore((state) =>
    pick(state, ['setAreaState'])
  )

  const { tbData, setTbData, selectedIds, setSelectedIds, ditto } =
    useTbViewStore((state) =>
      pick(state, [
        'tbData',
        'setTbData',
        'selectedIds',
        'setSelectedIds',
        'ditto',
      ])
    )

  const { tbs } = useAppEx()

  const del = () => {
    if (!selectedIds.length) return
    setOpen(true)
  }

  const closeBatchDelete = () => {
    setAreaState({ isBatchDelete: false })
  }

  const cancel = () => {
    flushSync(() => {
      setSelectedIds([])
    })
    closeBatchDelete()
    // 更新点位树
    if (Object.keys(delRef.current).length) {
      setTbData(tbData.filter((item) => !delRef.current[item.objectId]))
    }
  }

  const onClose = () => {
    if (status === 0) {
      setOpen(false)
    } else if (status === 1) {
      cancel()
    }
  }

  const createBindStat = (bindData, useInfo) => {
    const stat = {}

    // 判断已绑定案件的点位
    bindData.forEach((content, i) => {
      const { useTableId, useSlugs } = useInfo[i]
      const [key] = useSlugs

      stat[useTableId] = {}
      const setStat = (areaId, item) => {
        if (stat[useTableId][areaId]) {
          stat[useTableId][areaId].push(item)
        } else {
          stat[useTableId][areaId] = [item]
        }
      }
      content.forEach((item) => {
        if (Array.isArray(item[key])) {
          // 防止数组嵌套
          const final = item[key].flat(Infinity)
          selectedIds.forEach((areaId) => {
            if (final.includes(areaId)) {
              setStat(areaId, item)
            }
          })
        } else if (selectedIds.includes(item[key])) {
          setStat(item[key], item)
        }
      })
    })

    return stat
  }

  const splitList = (stat, areaCache) => {
    const notice = []
    const locked = {}

    // 创建提示列表
    Object.keys(stat).forEach((tbId) => {
      const areas = stat[tbId]
      const { tbName } = tbs[tbId]
      Object.keys(areas).forEach((areaId) => {
        locked[areaId] = true
        notice.push({
          areaId,
          areaName: areaCache[areaId].title,
          tbId,
          tbName,
          tableDatas: areas[areaId],
        })
      })
    })

    // 获取未绑定案件的点位
    const toDelete = selectedIds.filter((id) => !locked[id])

    return { notice, toDelete }
  }

  const createDeleteQueue = (toDelete, areaCache) => {
    const queue = []
    // 记录所有待删除的点
    const deleteCache = {}
    toDelete.forEach((id) => {
      deleteCache[id] = areaCache[id]
    })

    toDelete.forEach((id) => {
      const { parentId, level } = deleteCache[id]
      // 存在父级节点时子节点不需要传
      if (deleteCache[parentId]) return

      if (queue[level]) {
        queue[level].push(id)
      } else {
        queue[level] = [id]
      }
    })

    return queue
  }

  const confirm = async () => {
    if (status === 0) {
      setLoading(true)
      try {
        // 获取点位绑定表格
        const { useInfo } = await ditto.fetchFieldBindingTable()

        // 获取绑定案件
        const bindData = await ditto.fetchBindedTableData(useInfo, selectedIds)

        // 分析绑定关系
        const stat = createBindStat(bindData, useInfo)

        const areaCache = {}
        tbData.forEach((item) => {
          areaCache[item.objectId] = item
        })

        // 区分是否可以删除
        const { notice, toDelete } = splitList(stat, areaCache)

        // 删除点位
        if (toDelete.length) {
          const queue = createDeleteQueue(toDelete, areaCache)

          await ditto.destroyAreas(queue)

          Toast.success({
            title: '删除成功',
          })

          toDelete.forEach((id) => {
            delRef.current[id] = true
          })
        }

        if (notice.length) {
          // 有绑定案件的点位时打开提示弹窗
          setLockList(notice)
          setStatus(1)
        } else {
          // 无绑定时直接关闭
          cancel()
        }
      } catch (err) {
        Toast.error({
          title: err.message || '删除失败',
        })
        onClose()
      }

      setLoading(false)
    } else if (status === 1) {
      onClose()
    }
  }

  return (
    <>
      <Button size="sm" type="primary" onClick={del}>
        删除
      </Button>
      <Button size="sm" stroke onClick={cancel}>
        取消
      </Button>
      <Modal
        title="提示"
        open={open}
        onClose={onClose}
        size={modalSize[status]}
        footer={() => footer(confirm, onClose, loading)}
      >
        {status ? (
          <List
            data={lockList}
            type={type}
            closeBatchDelete={closeBatchDelete}
          />
        ) : (
          <Confirm type={type} />
        )}
      </Modal>
    </>
  )
}

export default memo(BatchDelete)
